<script setup>
import { nextTick, onMounted, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  length: { type: Number, default: 6 },
  disabled: { type: Boolean, default: false },
  autofocus: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue', 'complete'])

const digits = ref(Array.from({ length: props.length }, () => ''))
const inputs = ref([])

function syncFromModel() {
  const raw = String(props.modelValue || '').replace(/\D/g, '').slice(0, props.length)
  digits.value = Array.from({ length: props.length }, (_, i) => raw[i] || '')
}

watch(() => props.modelValue, syncFromModel)
watch(() => props.length, syncFromModel)

onMounted(() => {
  syncFromModel()
  if (props.autofocus) {
    nextTick(() => inputs.value[0]?.focus())
  }
})

function emitValue() {
  const code = digits.value.join('')
  emit('update:modelValue', code)
  if (code.length === props.length && digits.value.every(Boolean)) {
    emit('complete', code)
  }
}

function setDigit(index, char) {
  if (!/^\d$/.test(char)) return
  digits.value[index] = char
  emitValue()
  if (index < props.length - 1) {
    inputs.value[index + 1]?.focus()
  }
}

function onInput(index, e) {
  const v = String(e.target.value || '').replace(/\D/g, '')
  if (!v) {
    digits.value[index] = ''
    e.target.value = ''
    emitValue()
    return
  }
  // Вставка нескольких цифр в одну ячейку
  if (v.length > 1) {
    const chars = v.slice(0, props.length - index).split('')
    chars.forEach((c, i) => {
      digits.value[index + i] = c
    })
    emitValue()
    const next = Math.min(index + chars.length, props.length - 1)
    inputs.value[next]?.focus()
    e.target.value = digits.value[index] || ''
    return
  }
  setDigit(index, v)
  e.target.value = digits.value[index] || ''
}

function onKeydown(index, e) {
  if (e.key === 'Backspace') {
    if (e.target.value) {
      e.target.value = ''
      digits.value[index] = ''
      emitValue()
    } else if (digits.value[index]) {
      digits.value[index] = ''
      emitValue()
    } else if (index > 0) {
      digits.value[index - 1] = ''
      emitValue()
      inputs.value[index - 1]?.focus()
    }
    e.preventDefault()
    return
  }
  if (e.key === 'ArrowLeft' && index > 0) {
    inputs.value[index - 1]?.focus()
    e.preventDefault()
  }
  if (e.key === 'ArrowRight' && index < props.length - 1) {
    inputs.value[index + 1]?.focus()
    e.preventDefault()
  }
}

function onPaste(index, e) {
  const text = (e.clipboardData?.getData('text') || '').replace(/\D/g, '')
  if (!text) return
  e.preventDefault()
  const chars = text.slice(0, props.length - index).split('')
  chars.forEach((c, i) => {
    digits.value[index + i] = c
  })
  emitValue()
  const next = Math.min(index + chars.length, props.length - 1)
  inputs.value[next]?.focus()
}

function focusFirstEmpty() {
  const idx = digits.value.findIndex(d => !d)
  inputs.value[idx >= 0 ? idx : 0]?.focus()
}

defineExpose({ focusFirstEmpty })
</script>

<template>
  <div class="otp-cells" role="group" aria-label="Код из приложения">
    <input
      v-for="(_, i) in digits"
      :key="i"
      :ref="el => { if (el) inputs[i] = el }"
      class="otp-cell form-control"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="1"
      :disabled="disabled"
      :value="digits[i]"
      @input="onInput(i, $event)"
      @keydown="onKeydown(i, $event)"
      @paste="onPaste(i, $event)"
      @focus="$event.target.select()"
    />
  </div>
</template>

<style scoped>
.otp-cells {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.otp-cell {
  width: 2.75rem;
  height: 3rem;
  padding: 0;
  text-align: center;
  font-size: 1.35rem;
  font-weight: 600;
  letter-spacing: 0;
  border-radius: 0.5rem;
}

.otp-cell:focus {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.2);
}
</style>
